// Used in many components

import { ValueList } from "#core/models/valueList.interface";
import { KeyValue } from "@angular/common";
import { Injectable } from "@angular/core";
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { AssetAssociation, Edition, Level } from "@carnegie-learning/cure-types";

@Injectable()
export class FormHelper {
    selectedSubject: AssetAssociation | undefined;
    selectedEdition: Edition | undefined;

    visibilityOpts: KeyValue<string, string>[] = [];

    performTypeOpts: KeyValue<string, string>[] = [
        { key: "0", value: "Video" },
        { key: "1", value: "Image" },
        { key: "2", value: "Both Video and Image" },
    ];

    maxAttemptsOptions: ValueList[] = [
        { id: -1, value: "Unlimited" },
        { id: 1, value: "1" },
        { id: 2, value: "2" },
        { id: 3, value: "3" },
        { id: 4, value: "4" },
        { id: 5, value: "5" },
    ];

    constructor() {}

    /**
     * @method Validate
     * @description checks if a particular form has a specific form error
     * @param {FormGroup} form
     * @param {string} field
     * @param {string} type
     * @returns {boolean}
     */
    Validate(form: UntypedFormGroup, field: string, type: string): boolean {
        return !!form.get(field)?.touched && !!form.get(field)?.hasError(type);
    }

    setSubjectAndEdition(
        subjectId = "",
        editionId = "",
        programs: AssetAssociation[] = [],
        editions: Edition[] = []
    ): void {
        this.selectedSubject = programs.find((d) => d.subjectId === subjectId);
        this.selectedEdition = editions.find((d) => d.editionId === editionId);
    }
    getFormLevels(): Level[] {
        if (this.selectedEdition?.levelIds && this.selectedSubject?.levels) {
            const edition = this.selectedEdition;
            return this.selectedSubject.levels.filter((l) =>
                edition.levelIds?.includes(l.levelId)
            );
        } else {
            return [];
        }
    }

    // Only use the levels from the given Asset
    getFormLevelsForAssetsLevelIds(levelIds: string[] | undefined): Level[] {
        if (this.selectedEdition?.levelIds && this.selectedSubject?.levels) {
            const edition = this.selectedEdition;
            return this.selectedSubject.levels.filter(l => edition.levelIds?.includes(l.levelId) && levelIds?.includes(l.levelId));
        } else {
            return [];
        }
    }
    getFormUnits(): string[] {
        if (this.selectedEdition?.unitsAssignable && this.selectedSubject?.unitsAssignable) {
            // PAS-10777 Certain editions didn't have a fully complete list of the units assignable, 
            // so merge the 2 arrays for users to choose from
            return [...new Set([...this.selectedSubject.unitsAssignable, ...this.selectedEdition.unitsAssignable])]
        } else if (this.selectedEdition?.unitsAssignable) {
            return this.selectedEdition.unitsAssignable;
        } else if (this.selectedSubject?.unitsAssignable) {
            return this.selectedSubject.unitsAssignable;
        } else {
            return [];
        }
    }
    getVisibilityOpts(): KeyValue<string, string>[] {
        this.resetVisibilityOpts();
        if ((<any>window).isDemo) {
            return this.visibilityOpts.filter((o) => o.key !== "none");
        }
        return this.visibilityOpts;
    }

    resetVisibilityOpts(): void {
        this.visibilityOpts = [
            { key: "self", value: "Only Me" },
            { key: "school", value: "Teachers at my School" },
            { key: "district", value: "Teachers in my District" },
            { key: "none", value: "Passport Community" },
        ];
    }

    getPerformTypeOpts(): KeyValue<string, string>[] {
        return this.performTypeOpts;
    }

    bothPerformTypesSelected(key: string): boolean {
        return key === "2";
    }

    public noWhitespaceValidator(control: UntypedFormControl) {
        const isWhitespace =
            control.value?.length && (control.value || "").trim().length === 0;
        const isValid = !isWhitespace;
        return isValid ? null : { whitespace: true };
    }

    public noQuillEditorWhitespaceValidator(control: UntypedFormControl) {
        // check if return value contain HTML tag i.e. <p>
        if (/<[a-z][\s\S]*>/i.test(control.value)) {
            // remove tag for the actual input string.
            const str = control.value.replace(/(<([^>]+)>)/gi, "");
            const isWhitespace = (str || "").trim().length === 0;
            return isWhitespace ? { whitespace: true } : null;
        }
        return null;
    }

    public isValidUrl(_string: UntypedFormControl) {
        let url_string;
        try {
            url_string = new URL(_string.value);
        } catch (_) {
            return { invalidUrl: true };
        }
        if (
            url_string.protocol === "http:" ||
            url_string.protocol === "https:"
        ) {
            return null;
        } else {
            return { invalidUrl: true };
        }
    }

    public scorePossibleValidator(control: UntypedFormControl) {
        const value = control.value;
        console.log("score validator", value);
        // Regex below accepts number between 0 to 9999
        const validScorePossible =
            /^([1-9]|[1-9][0-9]|[1-9][0-9][0-9]|[1-9][0-9][0-9][0-9])$/g;
        const isValid = validScorePossible.test(value);
        return isValid ? null : { scorePossibleInvalid: true };
    }

    zeroLead(control: AbstractControl) {
        if (control?.value && /^0+(0$|[1-9])/.test(control.value)) {
            control.setValue(
                control.value.replace(/^(?:0+(?=[1-9])|0+(?=0$))/gm, "")
            );
        }
        return null;
    }

    getMaxAttemptsOptions(): ValueList[] {
        return this.maxAttemptsOptions;
    }

    removeInputWhiteSpaces(control: AbstractControl) {
        if (control?.value && /\s/g.test(control.value)) {
            control.setValue(control.value.replace(/\s+/g, ""));
        }
        return null;
    }

    public firstLetterNotCapitalizedValidator(control: UntypedFormControl) {
        const isLowerCase =
            control.value.charAt(0) === control.value.charAt(0).toLowerCase();
        return !isLowerCase ? null : { lowercase: true };
    }
}
